import React, { useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Stack,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  PhotoCamera as PhotoCameraIcon,
  PhotoAlbum as PhotoAlbumIcon,
  ArrowForward as ArrowForwardIcon,
  Delete as DeleteIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { useAlbum } from '../../contexts/AlbumContext';
import { useApi } from '../../hooks/useApi';

const UploadStep = () => {
  const fileInputRef = useRef(null);
  const {
    loading,
    albumName,
    setAlbumName,
    savedAlbums,
    loadAlbum,
    deleteAlbum,
  } = useAlbum();
  const { uploadPhotos } = useApi();

  const [nameConfirmed, setNameConfirmed] = useState(false);

  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      await uploadPhotos(files);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleConfirmName = () => {
    if (albumName.trim()) {
      setNameConfirmed(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && albumName.trim()) {
      handleConfirmName();
    }
  };

  const handleLoadAlbum = (albumId) => {
    loadAlbum(albumId);
  };

  const handleDeleteAlbum = (e, albumId) => {
    e.stopPropagation();
    deleteAlbum(albumId);
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 앨범명 입력 단계
  if (!nameConfirmed) {
    return (
      <Stack spacing={3}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <PhotoAlbumIcon
              sx={{ fontSize: 64, color: 'primary.main', mb: 2 }}
            />
            <Typography variant="h2" gutterBottom>
              새 앨범 만들기
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              여행 앨범의 이름을 입력해주세요.
            </Typography>

            <TextField
              fullWidth
              label="앨범 이름"
              placeholder="예: 태국 방콕 여행 2024"
              value={albumName}
              onChange={(e) => setAlbumName(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{ mb: 2 }}
              autoFocus
            />

            <Button
              variant="contained"
              size="large"
              onClick={handleConfirmName}
              disabled={!albumName.trim()}
              endIcon={<ArrowForwardIcon />}
              sx={{ px: 4, py: 1.5 }}
            >
              다음
            </Button>
          </CardContent>
        </Card>

        {/* 저장된 앨범 목록 */}
        {savedAlbums.length > 0 && (
          <Card>
            <CardContent sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FolderOpenIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  저장된 앨범
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                이전에 작업하던 앨범을 이어서 편집할 수 있습니다.
              </Typography>
            </CardContent>
            <Divider />
            <List disablePadding>
              {savedAlbums.map((album, idx) => (
                <React.Fragment key={album.id}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleLoadAlbum(album.id)}>
                      <ListItemText
                        primary={album.name}
                        secondary={formatDate(album.savedAt)}
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => handleDeleteAlbum(e, album.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItemButton>
                  </ListItem>
                  {idx < savedAlbums.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Card>
        )}
      </Stack>
    );
  }

  // 사진 업로드 단계
  return (
    <Stack spacing={3}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <PhotoCameraIcon
            sx={{ fontSize: 64, color: 'primary.main', mb: 2 }}
          />
          <Typography variant="h2" gutterBottom>
            {albumName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            여행 사진을 선택해주세요.
            <br />
            GPS 정보가 포함된 사진이 필요합니다.
          </Typography>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            accept="image/*"
            style={{ display: 'none' }}
          />

          <Button
            variant="contained"
            size="large"
            onClick={handleUploadClick}
            disabled={loading}
            startIcon={<CloudUploadIcon />}
            sx={{ px: 4, py: 1.5 }}
          >
            {loading ? '업로드 중...' : '사진 선택하기'}
          </Button>
        </CardContent>
      </Card>

      <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            지원 형식
          </Typography>
          <Typography variant="body2">
            JPG, JPEG, PNG, HEIC (GPS 메타데이터 포함)
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ px: 2 }}>
        <Typography variant="caption" color="text.secondary">
          * 사진의 EXIF 데이터를 분석하여 자동으로 장소와 시간을 파악합니다.
        </Typography>
      </Box>

      <Button
        variant="text"
        size="small"
        onClick={() => setNameConfirmed(false)}
        sx={{ alignSelf: 'center' }}
      >
        앨범 이름 변경
      </Button>
    </Stack>
  );
};

export default UploadStep;
